import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Target, Package, ClipboardList, TrendingUp, FlaskConical, ChevronLeft, ChevronRight, Route } from "lucide-react";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

const STAGES = [
  { n: "01", title: "The Methodology", sub: "Think · Say · Do", Icon: BookOpen },
  { n: "02", title: "The Market", sub: "Client · Competitor · Opportunity", Icon: Target },
  { n: "03", title: "The Product", sub: "Product · Promise · Proof", Icon: Package },
  { n: "04", title: "The Admin", sub: "Process · Platform · Precision", Icon: ClipboardList },
  { n: "05", title: "The Metrics", sub: "Measure · Monitor · Improve", Icon: TrendingUp },
  { n: "06", title: "The Lab", sub: "Review · Refine · Repeat", Icon: FlaskConical },
];

const DEFAULT_TESTIMONIALS = [
  { quote: "I closed my first deal in week three. The role-plays made real objections feel familiar.", author: "Sarah, Property Consultant" },
  { quote: "Finally an onboarding that treats sales like a craft, not a checklist.", author: "Marcus, Sales Lead" },
];

function TestimonialCarousel({ brand }) {
  const slides = (
    brand.testimonials?.length
      ? brand.testimonials
      : [{ quote: brand.testimonial_quote, author: brand.testimonial_author }, ...DEFAULT_TESTIMONIALS]
  ).filter((s) => s.quote);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setIdx((v) => (v + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  const cur = slides[idx] || slides[0];
  return (
    <div
      data-testid="testimonial-carousel"
      className="absolute bottom-6 left-6 right-6 backdrop-blur border hairline p-6"
      style={{ backgroundColor: "color-mix(in srgb, var(--brand-bg) 95%, transparent)" }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="label-eyebrow">Live cohort</div>
        {slides.length > 1 && (
          <div className="flex items-center gap-1">
            <button
              data-testid="testimonial-prev"
              aria-label="Previous testimonial"
              onClick={() => setIdx((v) => (v - 1 + slides.length) % slides.length)}
              className="p-1 text-neutral-400 hover:text-[var(--brand-primary)] transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              data-testid="testimonial-next"
              aria-label="Next testimonial"
              onClick={() => setIdx((v) => (v + 1) % slides.length)}
              className="p-1 text-neutral-400 hover:text-[var(--brand-primary)] transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
      <div key={idx} data-testid="testimonial-quote" className="font-display text-2xl leading-tight animate-in fade-in duration-500">
        &ldquo;{cur.quote}&rdquo;
      </div>
      <div className="mt-3 flex items-center justify-between gap-4">
        <div className="text-sm text-neutral-500" data-testid="testimonial-author">— {cur.author}</div>
        {slides.length > 1 && (
          <div className="flex items-center gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                aria-label={`Testimonial ${i + 1}`}
                onClick={() => setIdx(i)}
                className="w-1.5 h-1.5 rounded-full transition-colors"
                style={{ background: i === idx ? "var(--brand-primary)" : "color-mix(in srgb, var(--brand-primary) 25%, transparent)" }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Landing() {
  const { brand } = useWorkspace();
  const [tracks, setTracks] = useState([]);
  useEffect(() => {
    api.get("/tracks/public").then((r) => setTracks(r.data)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-[color:var(--brand-bg)] text-[var(--brand-primary)]">
      <header className="border-b hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center" style={{border:'1px solid var(--brand-primary)'}}>
              <span className="font-display font-semibold text-xs tracking-tight">{brand.logo_text}</span>
            </div>
            <div>
              <div className="font-display text-base leading-tight">{brand.name}</div>
              <div className="label-eyebrow" style={{fontSize:'0.55rem'}}>{brand.tagline || "Onboarding Portal"}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm link-underline" data-testid="nav-login">Sign in</Link>
            <Link to="/register" className="btn-primary text-sm" data-testid="nav-register">Get started</Link>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-24 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <div className="label-eyebrow mb-6">{brand.hero_eyebrow}</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            {brand.hero_title}
          </h1>
          <p className="mt-8 text-lg text-neutral-600 max-w-xl leading-relaxed">
            A structured, editorial onboarding portal for your new hires — with dedicated training tracks
            for every seat in the business. Staged curriculum, a living resource library,
            and live AI role-plays that build real conviction — not just knowledge.
          </p>
          {tracks.length > 0 && (
            <div className="mt-8" data-testid="landing-tracks">
              <div className="label-eyebrow mb-3 flex items-center gap-2">
                <Route size={12} strokeWidth={1.5} /> Training tracks
              </div>
              <div className="flex flex-wrap gap-2 max-w-xl">
                {tracks.map((t) => (
                  <span
                    key={t.name}
                    data-testid={`landing-track-${t.name}`}
                    title={t.description}
                    className="inline-flex items-center gap-2 border px-3 py-1.5 text-xs font-mono tracking-wide bg-white/60"
                    style={{ borderColor: "color-mix(in srgb, var(--brand-primary) 30%, transparent)" }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.accent || "var(--brand-primary)" }} />
                    {t.name}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/register" className="inline-flex items-center gap-2 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-hover)] text-white px-7 py-3.5 font-semibold rounded-sm transition-colors" data-testid="cta-start">
              Onboard Me <ArrowRight size={16} />
            </Link>
            <Link to="/login" className="btn-ghost inline-flex items-center gap-2" data-testid="cta-signin">
              I already have an account
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="relative aspect-[4/5] w-full overflow-hidden">
            <img
              src={brand.hero_image || "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1200&q=80"}
              alt="Onboarding cohort session"
              className="w-full h-full object-cover"
            />
            <TestimonialCarousel brand={brand} />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 border-t hairline">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <div className="label-eyebrow mb-4">The Learning Journey</div>
            <h2 className="font-display text-3xl lg:text-4xl leading-tight">
              Six stages, one <em className="italic">deliberate</em> sequence.
            </h2>
          </div>
          <div className="lg:col-span-7 flex items-end">
            <p className="text-neutral-500 max-w-xl">
              Each stage compounds on the one before it. Start with the mindset — finish in The Lab,
              reviewing real calls. Every trainee walks the same path so every trainee arrives at the same standard.
            </p>
          </div>
        </div>

        {/* Timeline stepper — horizontal on desktop, vertical on mobile */}
        <div className="relative">
          {/* Horizontal timeline line (desktop) */}
          <div className="hidden md:block absolute top-8 left-[5%] right-[5%] h-px" style={{backgroundColor:'color-mix(in srgb, var(--brand-primary) 25%, transparent)'}} aria-hidden="true"/>

          <ol className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-8 md:gap-4 relative">
            {STAGES.map(({ n, title, sub, Icon }, i) => (
              <li key={n} data-testid={`journey-step-${n}`} className="relative flex md:flex-col items-start md:items-center gap-4 md:gap-0">
                {/* Vertical line connector (mobile only) */}
                {i < STAGES.length - 1 && (
                  <div className="md:hidden absolute left-8 top-16 bottom-[-2rem] w-px" style={{backgroundColor:'color-mix(in srgb, var(--brand-primary) 25%, transparent)'}} aria-hidden="true"/>
                )}

                {/* Milestone node — solid emerald circle with white number */}
                <div className="relative flex-shrink-0 z-10">
                  <div className="w-16 h-16 rounded-full bg-[color:var(--brand-primary)] flex items-center justify-center font-display text-2xl text-white shadow-md ring-4 ring-[color:var(--brand-bg)]">
                    {n}
                  </div>
                </div>

                {/* Label */}
                <div className="md:mt-6 md:text-center flex-1">
                  <div className="label-eyebrow text-[var(--brand-primary)]" style={{fontSize:'0.6rem'}}>Stage {n}</div>
                  <div className="mt-1 font-display text-xl leading-tight">{title}</div>
                  <div className="mt-1 text-sm text-neutral-500 md:max-w-[10rem] md:mx-auto">{sub}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Footer nudge */}
        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 pt-8 border-t hairline">
          <div className="text-sm text-neutral-500">Complete in order for the certification track — or jump in wherever you need the reps.</div>
          <Link to="/register" className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="cta-start-journey">
            Start Stage 01 <ArrowRight size={14}/>
          </Link>
        </div>
      </section>

      <footer className="border-t hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 text-xs text-neutral-500 flex flex-wrap items-center justify-between gap-4">
          <div>© {new Date().getFullYear()} {brand.name}</div>
          <div className="label-eyebrow">Internal onboarding portal</div>
        </div>
      </footer>
    </div>
  );
}
